import { useId, useState, type InputHTMLAttributes } from 'react'

import { confirmSignUp, resendCode } from '../auth'
import { ProblemBanner } from './Problem'

export const PASSWORD_HINT = 'At least 12 characters, with upper case, lower case and a number.'

/** A labelled input. The hint is tied to the input so a screen reader reads it with it. */
export function Field({
  label,
  hint,
  ...input
}: { label: string; hint?: string } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId()
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} aria-describedby={hint ? `${id}-hint` : undefined} {...input} />
      {hint ? (
        <p id={`${id}-hint`} className="muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

/**
 * The "enter the code we emailed you" step, shared by sign-up and by a sign-in that
 * found the address unconfirmed. `onConfirmed` runs once the code is accepted; whatever
 * it rejects with is shown here.
 */
export function ConfirmCode({
  email,
  onConfirmed,
}: {
  email: string
  onConfirmed: () => Promise<void>
}) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    confirmSignUp(email, code.trim())
      .then(onConfirmed)
      .catch(setError)
      .finally(() => setBusy(false))
  }

  const resend = () => {
    setError(null)
    resendCode(email)
      .then(() => setSent(true))
      .catch(setError)
  }

  return (
    <form className="auth-form" onSubmit={submit}>
      <h1>Check your email</h1>
      <p>
        We sent a code to <strong>{email}</strong>. Enter it to confirm your address.
      </p>
      <ProblemBanner error={error} />
      {sent ? (
        <p className="notice good" role="status">
          If that address is waiting to be confirmed, a new code is on its way.
        </p>
      ) : null}
      <Field
        label="Confirmation code"
        value={code}
        onChange={(event) => setCode(event.target.value)}
        inputMode="numeric"
        autoComplete="one-time-code"
        required
        autoFocus
      />
      <div className="row">
        <button className="primary" type="submit" disabled={busy || !code.trim()}>
          {busy ? 'Confirming…' : 'Confirm'}
        </button>
        <button className="link" type="button" onClick={resend}>
          Send the code again
        </button>
      </div>
    </form>
  )
}
